import {
  ExtraSelectionType,
  RenderedExtraGroup,
} from '../../Model/Product-Interfaces';

/**
 * Extras parsing shared by the options modal (`recipe-option-modal`) and the
 * cards that open it. Moved out of the removed legacy `popup/popup.service.ts`
 * (Oct 2026) unchanged, apart from `popupData` becoming `OptionModalData`.
 */

/** What a card hands to `RecipeOptionModalService.open()` */
export interface OptionModalData {
  MenuBuilder: boolean;
  dineIn: boolean;
  eatIn: boolean;
  filteredProduct: any;
  filteredRecipe: any;
  renderedExtraList: any[];
  renderExtralistChooseOnes: number;
  openForNonExtras: boolean;
  quantity?: number;
  mergePopups?: boolean;
  renderedMenuBuilder?;
  isPromotionItem?: boolean;
  PromotionItemId?: number;
}

/* ------------------------------------------------------------------ */
/* Extras group selection criteria – derived from the group's key      */
/* ------------------------------------------------------------------ */

/**
 * Keywords in a group's key (its name in `RecipeExtraObject` /
 * `ProductExtraObject`) that pick its selection criteria, matched
 * case-insensitively and in this order (so "Select Optional Sauces" is
 * SelectOptional, not Optional), e.g. "Choose One", "Optional Protein",
 * "Select Optional Sauces". Anything else is Multiple.
 */
export const EXTRA_SELECTION_KEYWORDS: [ExtraSelectionType, string][] = [
  ['Single', 'choose'],
  ['SelectOptional', 'select optional'],
  ['Optional', 'optional'],
];

/** Keys of `RecipeExtraObject` / `ProductExtraObject` that are flags, not extras groups */
const EXTRA_OBJECT_META_KEYS = [
  'MenuBuilder',
  'mergePopups',
  'Sections',
  'MaxQuantity',
  'ShowProductsDetails',
];

/**
 * Resolves the selection criteria of an extras group from its key:
 *  - key contains "choose"          -> Single
 *  - key contains "select optional" -> SelectOptional (checkbox multi-select)
 *  - key contains "optional"        -> Optional
 * Keys with no keyword keep the legacy behaviour:
 *  - MaxQuantity of 1               -> Optional (radio, not required)
 *  - anything else                  -> Multiple
 */
export function resolveExtraSelectionType(group: any): ExtraSelectionType {
  const key = String(group?.Key ?? '').toLowerCase();
  const byKeyword = EXTRA_SELECTION_KEYWORDS.find(([, word]) =>
    key.includes(word)
  );
  if (byKeyword) return byKeyword[0];

  const maxQuantity = Number(group?.maxQuantity ?? group?.MaxQuantity);
  if (maxQuantity === 1) return 'Optional';

  return 'Multiple';
}

/** A group blocks "add to cart" until it has a selection only when it is `Single` */
export function isRequiredExtraGroup(group: any): boolean {
  return resolveExtraSelectionType(group) === 'Single';
}

/** Number of `Single` groups in a rendered extras list (drives "Please choose N more options") */
export function countRequiredExtraGroups(
  groups: any[] | null | undefined
): number {
  if (!Array.isArray(groups)) return 0;
  return groups.filter((group) => isRequiredExtraGroup(group)).length;
}

/**
 * Turns one `[key, value]` entry of an extra object into a rendered group.
 *
 * Supports both shapes the backend sends:
 *  - `"Choose One": [ {Title, Price, ...}, ... ]`
 *  - `"Toppings": { MaxQuantity: 3, Extras: [ ... ] }`
 *
 * Returns `null` for flag keys (`MenuBuilder`, `mergePopups`, `Sections`, ...),
 * for values that are not a list of extras, and for a group with no options
 * at all — the Menu Builder editor can leave an empty `"Select Option N": []`
 * behind, which would otherwise render as a step with nothing to pick (and,
 * named "Choose …", would count as required and block Add to cart for good).
 */
export function toRenderedExtraGroup(
  key: string,
  value: any
): RenderedExtraGroup | null {
  if (EXTRA_OBJECT_META_KEYS.includes(key)) return null;
  if (!value || typeof value !== 'object') return null;

  const isObjectForm = !Array.isArray(value) && Array.isArray(value.Extras);
  const extras: any[] = isObjectForm
    ? value.Extras
    : Array.isArray(value)
      ? value
      : null;
  if (!extras || !extras.length) return null;

  extras.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    item.Key = key;
    item.Quantity = 0;
    item.checked = false;
  });

  const group: RenderedExtraGroup = { Key: key, extras };

  if (isObjectForm) {
    const maxQuantity = Number(value.MaxQuantity);
    if (Number.isFinite(maxQuantity) && maxQuantity > 0) {
      group.maxQuantity = maxQuantity;
    }
  }

  return group;
}

/** Builds the full `renderedExtraList` for a parsed extra object */
export function toRenderedExtraList(extras: any): RenderedExtraGroup[] {
  if (!extras || typeof extras !== 'object') return [];
  const groups: RenderedExtraGroup[] = [];
  Object.entries(extras).forEach(([key, value]) => {
    const group = toRenderedExtraGroup(key, value);
    if (group) groups.push(group);
  });
  return groups;
}
